import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React, { useCallback, useState } from 'react'

import UnassignedChip from 'components/customer-details/content/work-orders/common/UnassignedChip'
import HHDesktopDatePicker from 'components/form-fields/v5/HHDesktopDatePicker'
import HHTextField from 'components/form-fields/v5/HHTextField'
import SelectItem from 'components/form-fields/v5/SelectItem'
import {
  outcomeOf,
  resolutionFor,
  retrievalNeedsYard,
  SETTLEMENT_OUTCOME_LABEL,
  SETTLEMENT_OUTCOMES,
} from 'components/inventory/containers/configured-service/deactivation/settlement'
import ServiceActionTypeChip from 'components/services/ServiceActionTypeChip'
import T from 'T'
import { Z_INDEX } from 'theme/zIndex'
import { formatDateToFEFormat } from 'utils/date'

import type { SettlementAction, SettlementWorkOrder, SettlementYard } from './ContainerSettlementList'
import type {
  RetrievalResolution,
  SettleableRow,
  SettlementOutcome,
} from 'components/inventory/containers/configured-service/deactivation/settlement'

export type NamedRow = SettleableRow & {
  containerName?: string | null
  serialNumber?: string | null
  groupLabel?: string | null
  groupCaption?: string | null
  groupKey?: string | null
}

const WIDTHS = { outcome: 190, date: 170, destination: 200, action: 64 }

const ELEVATED_MENU = { MenuProps: { sx: { zIndex: Z_INDEX.ELEVATED_DIALOG_MENU } } }
const ELEVATED_POPPER = { sx: { zIndex: Z_INDEX.ELEVATED_DIALOG_MENU } }

export const SETTLEMENT_COLUMNS = {
  display: 'grid',
  gridTemplateColumns: `minmax(180px, 1fr) ${WIDTHS.outcome}px ${WIDTHS.date}px ${WIDTHS.destination}px ${WIDTHS.action}px`,
  alignItems: 'center',
  columnGap: 1,
}

export const identityOf = (row: NamedRow) => [row.containerName, row.serialNumber].filter(Boolean).join(' · ')

export const groupKeyOf = (row?: NamedRow) => (row ? row.groupKey ?? row.groupLabel : undefined)

export const startsGroup = (rows: NamedRow[], index: number) =>
  Boolean(rows[index].groupLabel) && groupKeyOf(rows[index]) !== groupKeyOf(rows[index - 1])

const outcomeOptions = (outcomes: SettlementOutcome[]) =>
  outcomes.map(outcome => (
    <SelectItem key={outcome} value={outcome}>
      {SETTLEMENT_OUTCOME_LABEL[outcome]}
    </SelectItem>
  ))

const destinationOptions = (yards: SettlementYard[]) =>
  yards.map(yard => (
    <SelectItem key={yard.id} value={yard.id}>
      {yard.name}
    </SelectItem>
  ))

const WO_PREFIX = 'wo:'
const NEW_PREFIX = 'new:'

const woPickerValue = (r: { workOrderId?: string; actionId?: string }, woPfx = WO_PREFIX, newPfx = NEW_PREFIX) => {
  if (r.workOrderId) return `${woPfx}${r.workOrderId}`
  if (r.actionId) return `${newPfx}${r.actionId}`
  return ''
}
const parseWOPickerValue = (value: string): { workOrderId?: string; actionId?: string } => {
  if (value.startsWith(WO_PREFIX)) return { workOrderId: value.slice(WO_PREFIX.length) }
  if (value.startsWith(NEW_PREFIX)) return { actionId: value.slice(NEW_PREFIX.length) }

  return {}
}

const woPickerOptions = (workOrders: SettlementWorkOrder[], actions: SettlementAction[]) => {
  const items: React.ReactNode[] = []
  if (workOrders.length > 0) {
    items.push(<ListSubheader key="wo-header">Existing work orders</ListSubheader>)
    workOrders.forEach(wo => {
      items.push(
        <MenuItem key={`wo-${wo.workOrderId}`} value={`${WO_PREFIX}${wo.workOrderId}`}>
          <Stack alignItems="center" direction="row" spacing={0.5}>
            {wo.serviceDate && <Typography variant="body2">{formatDateToFEFormat(wo.serviceDate)}</Typography>}
            {wo.eventActionType && <ServiceActionTypeChip action={wo.eventActionType} label={wo.eventActionType} size="small" />}
            <UnassignedChip />
            <Typography color="text.secondary" variant="caption">
              {[wo.workOrderNumber, wo.workOrderStatus].filter(Boolean).join(' | ')}
            </Typography>
          </Stack>
        </MenuItem>
      )
    })
  }
  if (actions.length > 0) {
    items.push(<ListSubheader key="new-header">Schedule new work order</ListSubheader>)
    actions.forEach(action => {
      items.push(
        <MenuItem key={`new-${action.id}`} value={`${NEW_PREFIX}${action.id}`}>
          <ServiceActionTypeChip action={action.actionType} label={action.actionType} size="small" />
        </MenuItem>
      )
    })
  }

  return items
}

export const SettlementBulkControls = ({
  outstanding,
  yards,
  serviceActions = [],
  existingWorkOrders = [],
  disabled,
  onApply,
}: {
  outstanding: number
  yards: SettlementYard[]
  serviceActions?: SettlementAction[]
  existingWorkOrders?: SettlementWorkOrder[]
  disabled: boolean
  onApply: (resolution: RetrievalResolution) => void
}) => {
  const theme = useTheme()
  const [bulk, setBulk] = useState<Partial<RetrievalResolution>>({})

  const handleKindChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setBulk(resolutionFor(event.target.value as SettlementOutcome)),
    []
  )
  const handleDateChange = useCallback((date: Date | null) => setBulk(prev => ({ ...prev, date })), [])
  const handleYardChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setBulk(prev => ({ ...prev, yardId: event.target.value })),
    []
  )
  const handleWOPickerChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseWOPickerValue(event.target.value)
    setBulk(prev => ({ ...prev, actionId: parsed.actionId, workOrderId: parsed.workOrderId }))
  }, [])
  const handleApply = useCallback(() => {
    if (!bulk.kind) return
    onApply(bulk as RetrievalResolution)
    setBulk({})
  }, [bulk, onApply])

  return (
    <Stack
      alignItems="flex-start"
      direction="row"
      spacing={1}
      sx={{ bgcolor: 'background.default', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1.5 }}
    >
      <HHTextField
        select
        deprecatedLabel={false}
        disabled={disabled}
        label={T.OUTCOME}
        SelectProps={ELEVATED_MENU}
        size="small"
        sx={{ flex: 1 }}
        value={outcomeOf(bulk as RetrievalResolution)}
        onChange={handleKindChange}
      >
        {outcomeOptions(SETTLEMENT_OUTCOMES)}
      </HHTextField>
      {bulk.kind === 'scheduled' && (
        <HHDesktopDatePicker
          disabled={disabled}
          label={T.RETRIEVAL_DATE}
          PopperProps={ELEVATED_POPPER}
          TextFieldProps={{ size: 'small', deprecatedLabel: false }}
          value={bulk.date ?? null}
          onChange={handleDateChange}
        />
      )}
      {bulk.kind && retrievalNeedsYard(bulk.kind) && (
        <HHTextField
          select
          deprecatedLabel={false}
          disabled={disabled}
          label={T.DESTINATION}
          SelectProps={ELEVATED_MENU}
          size="small"
          sx={{ width: WIDTHS.destination }}
          value={bulk.yardId ?? ''}
          onChange={handleYardChange}
        >
          {destinationOptions(yards)}
        </HHTextField>
      )}
      {bulk.kind === 'scheduled' && (serviceActions.length > 0 || existingWorkOrders.length > 0) && (
        <HHTextField
          select
          deprecatedLabel={false}
          disabled={disabled}
          label={T.WORK_ORDER}
          SelectProps={ELEVATED_MENU}
          size="small"
          sx={{ width: 220 }}
          value={woPickerValue(bulk, WO_PREFIX, NEW_PREFIX)}
          onChange={handleWOPickerChange}
        >
          {woPickerOptions(existingWorkOrders, serviceActions)}
        </HHTextField>
      )}
      <Button disabled={disabled || !bulk.kind} sx={{ mt: 0.5 }} variant="outlined" onClick={handleApply}>
        {`${T.APPLY} ${T.TO.toLowerCase()} ${outstanding}`}
      </Button>
    </Stack>
  )
}

export const SettlementRow = ({
  row,
  resolution,
  yards,
  serviceActions = [],
  existingWorkOrders = [],
  disabled,
  offeredOutcomes = SETTLEMENT_OUTCOMES,
  onSet,
}: {
  row: NamedRow
  resolution?: RetrievalResolution
  yards: SettlementYard[]
  serviceActions?: SettlementAction[]
  existingWorkOrders?: SettlementWorkOrder[]
  disabled: boolean
  offeredOutcomes?: SettlementOutcome[]
  onSet: (id: string, patch: Partial<RetrievalResolution>) => void
}) => {
  const id = String(row.id)
  const hasWOOptions = serviceActions.length > 0 || existingWorkOrders.length > 0
  const needsYard = resolution?.kind ? retrievalNeedsYard(resolution.kind) : false
  const showDate = resolution?.kind === 'scheduled' || resolution?.kind === 'retrieved'

  const handleKindChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onSet(id, resolutionFor(event.target.value as SettlementOutcome)),
    [id, onSet]
  )
  const handleDateChange = useCallback((date: Date | null) => onSet(id, { date }), [id, onSet])
  const handleDestinationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onSet(id, { yardId: event.target.value }),
    [id, onSet]
  )
  const handleWOPickerChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = parseWOPickerValue(event.target.value)
      onSet(id, { actionId: parsed.actionId ?? undefined, workOrderId: parsed.workOrderId ?? undefined })
    },
    [id, onSet]
  )

  const rowWoPickerValue = woPickerValue(resolution ?? {})

  return (
    <Box sx={{ px: 1, py: 1 }}>
      <Box sx={SETTLEMENT_COLUMNS}>
        <Typography noWrap title={identityOf(row)} variant="body2">
          {identityOf(row)}
        </Typography>

        <HHTextField
          select
          deprecatedLabel={false}
          disabled={disabled}
          label=""
          placeholder={T.SET_OUTCOME}
          SelectProps={ELEVATED_MENU}
          size="small"
          value={outcomeOf(resolution)}
          onChange={handleKindChange}
        >
          {outcomeOptions(offeredOutcomes)}
        </HHTextField>

        {showDate ? (
          <HHDesktopDatePicker
            disabled={disabled}
            PopperProps={ELEVATED_POPPER}
            TextFieldProps={{ size: 'small', deprecatedLabel: false, sx: { width: WIDTHS.date } }}
            value={resolution?.date ?? null}
            onChange={handleDateChange}
          />
        ) : (
          <Box />
        )}

        {needsYard ? (
          <HHTextField
            select
            deprecatedLabel={false}
            disabled={disabled}
            label=""
            placeholder={T.DESTINATION}
            SelectProps={ELEVATED_MENU}
            size="small"
            value={resolution?.yardId ?? ''}
            onChange={handleDestinationChange}
          >
            {destinationOptions(yards)}
          </HHTextField>
        ) : (
          <Box />
        )}

        <Box />
      </Box>

      {resolution?.kind === 'scheduled' && hasWOOptions && (
        <Box sx={{ mt: 0.75, ml: 1 }}>
          <HHTextField
            select
            deprecatedLabel={false}
            disabled={disabled}
            label={T.WORK_ORDER}
            SelectProps={ELEVATED_MENU}
            size="small"
            sx={{ minWidth: 360 }}
            value={rowWoPickerValue}
            onChange={handleWOPickerChange}
          >
            {woPickerOptions(existingWorkOrders, serviceActions)}
          </HHTextField>
        </Box>
      )}
    </Box>
  )
}
